import { i18n } from "@lingui/core";
import useAccountInfo from "@/hooks/useAccountInfo";
import { api } from "@/lib/api-client";
import type { GetConsent } from "@/server-types";
import {
  CONSENT_VERSION,
  type ConsentCategories,
  type ConsentSource,
  hasSessionToken,
  type PersistableConsentSource,
  readComplianzConsent,
  readConsentCookie,
  resolveAuthenticatedConsent,
  writeConsentCookie,
} from "@/utils/consent";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

type ServerConsentSnapshot = GetConsent["response"];

type ConsentContextValue = ConsentCategories & {
  decided: boolean;
  hydrated: boolean;
  preferencesOpen: boolean;
  save: (categories: ConsentCategories) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  openPreferences: () => void;
  closePreferences: () => void;
  reconcileWithServer: (server: ServerConsentSnapshot | null) => void;
  impersonated: boolean;
  /**
   * The wording to display, served by the API so it is the same string the
   * consent ledger stores. Null until it arrives.
   */
  statement: string | null;
  /** A page shows the banner inline, so the floating one stays hidden. */
  isBannerEmbedded: boolean;
  setBannerEmbedded: (isEmbedded: boolean) => void;
};

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [decided, setDecided] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [isBannerEmbedded, setBannerEmbedded] = useState(false);

  // Kept in refs so `reconcileWithServer` can stay identity-stable (its effect
  // fires off the server snapshot, not off local state changes).
  const decidedRef = useRef(false);
  const analyticsRef = useRef(false);
  const marketingRef = useRef(false);
  const sourceRef = useRef<ConsentSource>("dashboard_banner");

  // Consent is recorded against the impersonated user, so an admin acting in
  // their session must never be asked for — or file — a decision as them.
  const impersonated = useAccountInfo((state) =>
    Boolean(state.accountInfo?.superAdmin),
  );
  const impersonatedRef = useRef(false);
  useEffect(() => {
    impersonatedRef.current = impersonated;
  }, [impersonated]);

  const { mutate: postConsent } = api.consent.usePostConsent();
  const statementLanguage = i18n.locale as Parameters<
    typeof api.consent.useGetConsentStatement
  >[0];
  const { data: statementData } =
    api.consent.useGetConsentStatement(statementLanguage);
  const statement = statementData?.text ?? null;

  const persistToServer = useCallback(
    (categories: ConsentCategories, source: PersistableConsentSource) => {
      if (!hasSessionToken() || impersonatedRef.current) return;
      postConsent({
        analytics: categories.analytics,
        marketing: categories.marketing,
        version: CONSENT_VERSION,
        source,
        language: statementLanguage,
      });
    },
    [postConsent, statementLanguage],
  );

  const applyDecision = useCallback(
    (
      categories: ConsentCategories,
      source: PersistableConsentSource,
      {
        persistCookie,
        persistRemote,
      }: { persistCookie: boolean; persistRemote: boolean },
    ) => {
      if (persistCookie) writeConsentCookie(categories);

      analyticsRef.current = categories.analytics;
      marketingRef.current = categories.marketing;
      decidedRef.current = true;
      sourceRef.current = source;

      setAnalytics(categories.analytics);
      setMarketing(categories.marketing);
      setDecided(true);

      if (persistRemote) persistToServer(categories, source);
    },
    [persistToServer],
  );

  useEffect(() => {
    const authenticated = hasSessionToken();

    const stored = readConsentCookie();
    if (stored) {
      // A per-browser cookie, possibly set by another user. Record it locally so
      // the reconciler can see it, but for an authenticated user the DB is
      // authoritative: keep trackers denied and the banner deferred until
      // `reconcileWithServer` runs (which flips `hydrated`). Only the
      // unauthenticated login page trusts the cookie on its own.
      analyticsRef.current = stored.analytics;
      marketingRef.current = stored.marketing;
      decidedRef.current = true;
      sourceRef.current = "cookie";
      if (!authenticated) {
        setAnalytics(stored.analytics);
        setMarketing(stored.marketing);
        setDecided(true);
        setHydrated(true);
      }
      return;
    }

    const complianz = readComplianzConsent();
    if (complianz && !authenticated) {
      // Login page: adopt a decision already made through the marketing-site
      // Complianz banner. Authenticated users import it via the reconciler
      // instead, so it can be checked against the DB first.
      applyDecision(complianz, "complianz", {
        persistCookie: true,
        persistRemote: false,
      });
    }

    if (!authenticated) setHydrated(true);
  }, [applyDecision]);

  const save = useCallback(
    (categories: ConsentCategories) => {
      applyDecision(categories, "dashboard_banner", {
        persistCookie: true,
        persistRemote: true,
      });
      setPreferencesOpen(false);
    },
    [applyDecision],
  );

  const acceptAll = useCallback(
    () => save({ analytics: true, marketing: true }),
    [save],
  );
  const rejectAll = useCallback(
    () => save({ analytics: false, marketing: false }),
    [save],
  );

  const openPreferences = useCallback(() => setPreferencesOpen(true), []);
  const closePreferences = useCallback(() => setPreferencesOpen(false), []);

  const reconcileWithServer = useCallback(
    (server: ServerConsentSnapshot | null) => {
      const complianz = readComplianzConsent();
      const action = resolveAuthenticatedConsent({
        serverDecided: server?.decided ?? false,
        localDecided: decidedRef.current,
        localSource: sourceRef.current,
        complianz,
      });

      switch (action.kind) {
        case "apply-server":
          // The user's own recorded decision wins; sync it to the cookie for
          // fast gating, but don't re-write the ledger.
          if (server) {
            applyDecision(
              { analytics: server.analytics, marketing: server.marketing },
              "dashboard_banner",
              { persistCookie: true, persistRemote: false },
            );
          }
          break;
        case "apply-complianz":
          if (complianz) {
            applyDecision(complianz, "complianz", {
              persistCookie: true,
              persistRemote: true,
            });
          }
          break;
        case "keep":
          break;
        case "reset":
          // The user has no recorded decision and none was made this session:
          // never inherit another session's cookie — force the banner and opt
          // trackers out. Nothing is written to the DB.
          analyticsRef.current = false;
          marketingRef.current = false;
          decidedRef.current = false;
          setAnalytics(false);
          setMarketing(false);
          setDecided(false);
          break;
      }

      // The authoritative snapshot has now been applied (or a decision kept), so
      // trackers/banner may activate. For an authenticated user this is the only
      // place `hydrated` flips — the mount effect deferred it to here.
      setHydrated(true);
    },
    [applyDecision],
  );

  return (
    <ConsentContext.Provider
      value={{
        analytics,
        marketing,
        decided,
        hydrated,
        preferencesOpen,
        save,
        acceptAll,
        rejectAll,
        openPreferences,
        closePreferences,
        reconcileWithServer,
        impersonated,
        statement,
        isBannerEmbedded,
        setBannerEmbedded,
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
}

export function useConsent(): ConsentContextValue {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error("useConsent must be used within ConsentProvider");
  }
  return context;
}
